import { ref } from 'vue'
import client from '@/lib/API/client'
import { readNotificationsCopy, writeNotificationsCopy } from '@/lib/sqlite/features/notifications-feature'

// Subject 7: the phone's copy of GET /notifications/config (see ./config.js),
// kept on the phone so the reminders and the protocol cues follow the admin
// without network, however long since the last change. Purged at logout.
const COPY_KEY = 'config'

/** The config in use: the last one synced, null while never synced (the app's defaults). */
export const phoneConfig = ref(null)

let loaded = null
// Bumped at logout: a read or a sync still in flight then keeps nothing.
let generation = 0

/** The copy on the phone, read once. Never throws. */
export function loadPhoneConfig() {
    const reading = generation
    loaded ??= readNotificationsCopy(COPY_KEY)
        .then((copy) => {
            if (copy && reading === generation && phoneConfig.value === null) phoneConfig.value = copy
        })
        .catch(() => undefined)
    return loaded.then(() => phoneConfig.value)
}

/**
 * Asks the API whether the config changed since the copy (by its version), and
 * keeps the new one. Offline or refused, the copy stays as it is. Never throws.
 */
export async function syncPhoneConfig() {
    await loadPhoneConfig()
    const syncing = generation
    try {
        const { data } = await client.get('/notifications/config', {
            params: phoneConfig.value?.version ? { version: phoneConfig.value.version } : {},
            // Bounded: the reminders are re-armed behind it on every foreground.
            timeout: 8000,
        })
        if (data?.types && !data.unchanged && syncing === generation) {
            phoneConfig.value = data
            await writeNotificationsCopy(COPY_KEY, data).catch(() => undefined)
        }
    } catch {
        // offline, signed out: keep the copy
    }
    return phoneConfig.value
}

/** Signed out: back to the app's defaults (the copy is purged by the logout itself). */
export function forgetPhoneConfig() {
    generation += 1
    phoneConfig.value = null
    loaded = Promise.resolve()
}
